import { useRef, useState, useEffect, type DragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, Marker, TileLayer, Popup, Tooltip, useMap } from "react-leaflet";
import { whatsappLink, isDesktopScan } from "@petloc/shared";
import { api } from "../../lib/api";
import { humanDate, fullTimestamp } from "../../lib/humanDate";

type Species = "dog" | "cat" | "dragon";

const SPECIES: { value: Species; emoji: string; key: string }[] = [
  { value: "dog", emoji: "🐕", key: "cats.speciesDog" },
  { value: "cat", emoji: "🐈", key: "cats.speciesCat" },
  { value: "dragon", emoji: "🐉", key: "cats.speciesDragon" },
];

interface Cat {
  id: string;
  name: string;
  species: Species;
  motto: string | null;
  steckbrief: string | null;
  visibleName: boolean;
  visibleMotto: boolean;
  visibleSteckbrief: boolean;
  visiblePhotos: boolean;
  showDistanceToFinder: boolean;
  smsEnabled: boolean;
  active: boolean;
  missing: boolean;
  photos: { id: string; url: string; isPrimary: boolean }[];
}

interface Finding {
  id: string;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  distanceKm: number | null;
  locationAddress: string | null;
  message: string | null;
  photoUrl: string | null;
  finderPhone: string | null;
  finderWhatsapp: boolean;
  notifiedSms: boolean;
  createdAt: string;
}

interface ScanEvent {
  id: string;
  createdAt: string;
  finderDevice: string | null;
}

type OrderStatus = "PAID" | "PROCESSING" | "SHIPPED" | "CANCELLED";

interface Order {
  id: string;
  status: OrderStatus;
  priceCents: number;
  createdAt: string;
  collars: { id: string; color: string; cat: { id: string; name: string } }[];
}

const EMPTY = {
  name: "",
  species: "cat" as Species,
  motto: "",
  steckbrief: "",
  visibleName: true,
  visibleMotto: true,
  visibleSteckbrief: true,
  visiblePhotos: true,
  showDistanceToFinder: true,
  smsEnabled: false,
};

interface PendingPhoto {
  file: File;
  previewUrl: string;
}

async function uploadPhotosTo(catId: string, files: File[]) {
  if (files.length === 0) return;
  const fd = new FormData();
  files.forEach((f) => fd.append("photos", f));
  await api(`/api/pets/${catId}/photos`, { method: "POST", body: fd });
}

export function CatEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const q = useQuery({
    queryKey: ["cat", id],
    queryFn: () => api<Cat>(`/api/pets/${id}`),
    enabled: !isNew,
  });

  const findingsQ = useQuery({
    queryKey: ["findings", id],
    queryFn: () => api<Finding[]>(`/api/pets/${id}/findings`),
    enabled: !isNew,
  });

  const scansQ = useQuery({
    queryKey: ["scans", id],
    queryFn: () => api<ScanEvent[]>(`/api/pets/${id}/scans`),
    enabled: !isNew,
  });

  const ordersQ = useQuery({
    queryKey: ["orders"],
    queryFn: () => api<Order[]>("/api/orders"),
    enabled: !isNew,
  });

  const [form, setForm] = useState<typeof EMPTY>(EMPTY);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState("");

  useEffect(() => {
    if (q.data) {
      setForm({
        name: q.data.name,
        species: q.data.species ?? "cat",
        motto: q.data.motto ?? "",
        steckbrief: q.data.steckbrief ?? "",
        visibleName: q.data.visibleName,
        visibleMotto: q.data.visibleMotto,
        visibleSteckbrief: q.data.visibleSteckbrief,
        visiblePhotos: q.data.visiblePhotos,
        showDistanceToFinder: q.data.showDistanceToFinder ?? true,
        smsEnabled: q.data.smsEnabled,
      });
    }
  }, [q.data]);

  useEffect(() => {
    return () => {
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
  }, [pendingPhotos]);

  useEffect(() => {
    if (window.location.hash === "#findings" && findingsQ.data?.length) {
      requestAnimationFrame(() => {
        document.getElementById("findings")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [findingsQ.data]);

  const addPending = (files: File[]) => {
    if (files.length === 0) return;
    const next = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingPhotos((cur) => [...cur, ...next]);
  };

  const removePending = (index: number) => {
    setPendingPhotos((cur) => {
      const removed = cur[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return cur.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = useMutation({
    mutationFn: (files: File[]) => uploadPhotosTo(id!, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cat", id] }),
    onError: (err) => console.error("Foto-Upload fehlgeschlagen:", err),
  });

  // Single entry point for both the file picker and drag & drop.
  const handleFiles = (files: File[]) => {
    if (files.length === 0) return;
    if (isNew) addPending(files);
    else uploadPhotos.mutate(files);
  };

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    handleFiles(files);
  };

  const deletePhoto = useMutation({
    mutationFn: (photoId: string) =>
      api(`/api/pets/${id}/photos/${photoId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cat", id] }),
    onError: (err) => console.error("Foto-Löschen fehlgeschlagen:", err),
  });

  const makePrimary = useMutation({
    mutationFn: (photoId: string) =>
      api(`/api/pets/${id}/photos/${photoId}/primary`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cat", id] }),
    onError: (err) => console.error("Hauptfoto setzen fehlgeschlagen:", err),
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        motto: form.motto || null,
        steckbrief: form.steckbrief || null,
      };
      if (isNew) {
        const created = await api<Cat>("/api/pets", {
          method: "POST",
          body: JSON.stringify(body),
        });
        await uploadPhotosTo(created.id, pendingPhotos.map((p) => p.file));
        return created;
      } else {
        // Tierart ist unveränderbar -> nicht mitsenden.
        const { species: _omitSpecies, ...patchBody } = body;
        return api<Cat>(`/api/pets/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patchBody),
        });
      }
    },
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      qc.invalidateQueries({ queryKey: ["cat"] });
      if (isNew) {
        pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        setPendingPhotos([]);
        nav(`/app/pets/${cat.id}`);
      }
    },
  });

  const deactivate = useMutation({
    mutationFn: () => api(`/api/pets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      qc.invalidateQueries({ queryKey: ["cat", id] });
    },
  });

  const reactivate = useMutation({
    mutationFn: () => api(`/api/pets/${id}/reactivate`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      qc.invalidateQueries({ queryKey: ["cat", id] });
    },
  });

  const markMissing = useMutation({
    mutationFn: () => api(`/api/pets/${id}/missing`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      qc.invalidateQueries({ queryKey: ["cat", id] });
    },
  });

  const markFound = useMutation({
    mutationFn: () => api(`/api/pets/${id}/found`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      qc.invalidateQueries({ queryKey: ["cat", id] });
    },
  });

  const permanentDelete = useMutation({
    mutationFn: () =>
      api(`/api/pets/${id}/permanent`, {
        method: "DELETE",
        body: JSON.stringify({ confirmName: confirmDelete.trim() }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cats"] });
      nav("/app");
    },
  });

  const deleteFinding = useMutation({
    mutationFn: (findingId: string) =>
      api(`/api/pets/${id}/findings/${findingId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["findings", id] });
      qc.invalidateQueries({ queryKey: ["cat", id] });
    },
  });

  const deleteScan = useMutation({
    mutationFn: (scanId: string) => api(`/api/pets/${id}/scans/${scanId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scans", id] }),
  });

  const existingPhotos = q.data?.photos ?? [];
  const hasAnyPhoto = isNew ? pendingPhotos.length > 0 : existingPhotos.length > 0;
  const findings = findingsQ.data ?? [];

  const profileCard = (
    <div className="card p-6 space-y-6">
      <Field label={t("cats.name")}>
        <input
          required
          maxLength={60}
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>

      <div>
        <div className="label">{t("cats.species")}</div>
        {isNew ? (
          <div className="grid grid-cols-3 gap-2">
            {SPECIES.map((s) => {
              const selected = form.species === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setForm({ ...form, species: s.value })}
                  className={`flex flex-col items-center gap-1 rounded-lg border py-3 text-sm transition-colors ${
                    selected
                      ? "border-brand-500 bg-brand-50 text-brand-700 font-medium"
                      : "border-slate-200 text-slate-600 hover:border-brand-300"
                  }`}
                >
                  <span className="text-2xl">{s.emoji}</span>
                  {t(s.key)}
                </button>
              );
            })}
          </div>
        ) : (
          // Tierart steht nach der Anlage fest -> nur anzeigen.
          <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <span className="text-xl">
              {SPECIES.find((s) => s.value === form.species)?.emoji ?? "🐾"}
            </span>
            {t(SPECIES.find((s) => s.value === form.species)?.key ?? "cats.speciesCat")}
          </div>
        )}
      </div>

      <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        <div className="flex items-center justify-between mb-3">
          <div className="label mb-0">{t("cats.photos")}</div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              // Snapshot files to File[] BEFORE clearing the input value,
              // otherwise the FileList can get invalidated mid-mutation.
              const files = Array.from(e.target.files ?? []);
              e.target.value = "";
              handleFiles(files);
            }}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => fileRef.current?.click()}
            disabled={!isNew && uploadPhotos.isPending}
          >
            {uploadPhotos.isPending ? t("common.loading") : `+ ${t("cats.addPhoto")}`}
          </button>
        </div>

        {!hasAnyPhoto ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className={`w-full aspect-[3/1] border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
              dragActive
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-slate-300 text-slate-500 hover:bg-slate-50 hover:border-brand-400"
            }`}
          >
            <div className="text-4xl mb-1">🐾</div>
            <div className="text-sm font-medium">{t("cats.addPhoto")}</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Foto(s) wählen oder hierher ziehen
            </div>
          </button>
        ) : (
          <div
            className={`grid grid-cols-3 gap-3 rounded-lg transition-shadow ${
              dragActive ? "ring-2 ring-brand-400 ring-offset-2" : ""
            }`}
          >
            {isNew
              ? pendingPhotos.map((p, i) => (
                  <div key={p.previewUrl} className="relative group">
                    <img
                      src={p.previewUrl}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removePending(i)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 text-sm leading-none hover:bg-red-700"
                      aria-label={t("cats.deletePhoto")}
                    >
                      ×
                    </button>
                    {i === 0 && (
                      <div className="absolute bottom-1 left-1 bg-brand-600 text-white text-[10px] rounded px-1.5 py-0.5">
                        {t("cats.makePrimary")}
                      </div>
                    )}
                  </div>
                ))
              : existingPhotos.map((p) => (
                  <div key={p.id} className="relative">
                    <img
                      src={p.url}
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    {/* Always-visible icon buttons — group-hover doesn't work on touch */}
                    <button
                      type="button"
                      onClick={() => deletePhoto.mutate(p.id)}
                      className="absolute top-1 right-1 z-10 bg-black/60 hover:bg-red-600 text-white rounded-full w-7 h-7 text-base leading-none backdrop-blur-sm flex items-center justify-center"
                      aria-label={t("cats.deletePhoto")}
                    >
                      ×
                    </button>
                    {!p.isPrimary && (
                      <button
                        type="button"
                        onClick={() => makePrimary.mutate(p.id)}
                        className="absolute top-1 left-1 z-10 bg-black/60 hover:bg-brand-600 text-white rounded-full w-7 h-7 text-sm leading-none backdrop-blur-sm flex items-center justify-center"
                        aria-label={t("cats.makePrimary")}
                        title={t("cats.makePrimary") ?? ""}
                      >
                        ☆
                      </button>
                    )}
                    {p.isPrimary && (
                      <div className="absolute top-1 left-1 z-10 bg-brand-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm shadow">
                        ★
                      </div>
                    )}
                  </div>
                ))}
          </div>
        )}
        {isNew && pendingPhotos.length > 0 && (
          <p className="text-xs text-slate-500 mt-2">
            Das erste Foto wird das Hauptfoto. Du kannst das später ändern.
          </p>
        )}
      </div>

      <hr className="border-slate-200" />

      <Field label={t("cats.motto")} hint={t("cats.mottoHint")}>
        <input
          maxLength={80}
          className="input"
          value={form.motto}
          onChange={(e) => setForm({ ...form, motto: e.target.value })}
        />
      </Field>
      <Field label={t("cats.steckbrief")} hint={t("cats.steckbriefHint")}>
        <textarea
          maxLength={1000}
          rows={4}
          className="input"
          value={form.steckbrief}
          onChange={(e) => setForm({ ...form, steckbrief: e.target.value })}
        />
      </Field>

      <div>
        <div className="label">{t("cats.visibility")}</div>
        <div className="grid grid-cols-2 gap-2">
          <Checkbox label={t("cats.visibleName")} checked={form.visibleName} onChange={(v) => setForm({ ...form, visibleName: v })} />
          <Checkbox label={t("cats.visibleMotto")} checked={form.visibleMotto} onChange={(v) => setForm({ ...form, visibleMotto: v })} />
          <Checkbox label={t("cats.visibleSteckbrief")} checked={form.visibleSteckbrief} onChange={(v) => setForm({ ...form, visibleSteckbrief: v })} />
          <Checkbox label={t("cats.visiblePhotos")} checked={form.visiblePhotos} onChange={(v) => setForm({ ...form, visiblePhotos: v })} />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <Checkbox
          label="Finder darf die Luftliniendistanz zu mir sehen"
          checked={form.showDistanceToFinder}
          onChange={(v) => setForm({ ...form, showDistanceToFinder: v })}
        />
        <p className="mt-1.5 pl-6 text-xs text-slate-500">
          Zeigt dem Finder nur die ungefähre Entfernung in km („ca. 1,2 km von zu Hause"). Deine{" "}
          <strong>Adresse wird niemals geteilt</strong>. In deiner Übersicht siehst du die Distanz immer.
        </p>
      </div>

      <Checkbox
        label={t("cats.smsEnabled")}
        checked={form.smsEnabled}
        onChange={(v) => setForm({ ...form, smsEnabled: v })}
      />

      <div className="flex flex-wrap gap-3 pt-2">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? t("common.loading") : t("cats.save")}
        </button>
        {!isNew && q.data?.active && (
          <button className="btn-ghost text-red-600" onClick={() => deactivate.mutate()}>
            {t("cats.deactivate")}
          </button>
        )}
        {!isNew && q.data && !q.data.active && (
          <button className="btn-secondary" onClick={() => reactivate.mutate()}>
            {t("cats.reactivate")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl">
      <Link to="/app" className="text-sm text-slate-500">
        ← {t("common.back")}
      </Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-2 mb-6">
        {isNew ? t("cats.new") : q.data?.name}
      </h1>

      {/* Vermisst-Status: im Notfall sofort auffindbar, ganz oben. */}
      {!isNew && q.data && (
        q.data.missing ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2 font-semibold text-red-800">
              <span aria-hidden>⚠️</span> {t("cats.missingStatus")}
            </div>
            <button
              type="button"
              onClick={() => markFound.mutate()}
              disabled={markFound.isPending}
              className="btn-secondary shrink-0 disabled:opacity-50"
            >
              {markFound.isPending ? t("common.loading") : t("cats.markFound")}
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => markMissing.mutate()}
              disabled={markMissing.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              🔍 {markMissing.isPending ? t("common.loading") : t("cats.markMissing")}
            </button>
          </div>
        )
      )}

      {isNew ? (
        <div className="max-w-3xl">{profileCard}</div>
      ) : (
        <>
          {q.data && <OrderPromoCard catName={q.data.name} catId={q.data.id} />}
          {q.data && (
            <ActiveOrdersInline
              catId={q.data.id}
              orders={(ordersQ.data ?? []).filter(
                (o) =>
                  (o.status === "PAID" || o.status === "PROCESSING") &&
                  o.collars.some((c) => c.cat.id === q.data!.id),
              )}
            />
          )}
          {q.data && <CollarsSection catId={q.data.id} />}
          {/* Sobald es Fundmeldungen gibt, sind sie das Wichtigste: nach links
              (Desktop) bzw. nach oben (Mobile) — das Profil ist dann längst gepflegt. */}
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {findings.length > 0 ? (
              <>
                <FindingsSection
                  findings={findings}
                  scans={scansQ.data ?? []}
                  onOpenPhoto={setLightboxUrl}
                  onDelete={(fid) => deleteFinding.mutate(fid)}
                  deletingId={deleteFinding.isPending ? (deleteFinding.variables ?? null) : null}
                  onDeleteScan={(sid) => deleteScan.mutate(sid)}
                  deletingScanId={deleteScan.isPending ? (deleteScan.variables ?? null) : null}
                />
                <div>{profileCard}</div>
              </>
            ) : (
              <>
                <div>{profileCard}</div>
                <FindingsSection
                  findings={findings}
                  scans={scansQ.data ?? []}
                  onOpenPhoto={setLightboxUrl}
                  onDelete={(fid) => deleteFinding.mutate(fid)}
                  deletingId={deleteFinding.isPending ? (deleteFinding.variables ?? null) : null}
                  onDeleteScan={(sid) => deleteScan.mutate(sid)}
                  deletingScanId={deleteScan.isPending ? (deleteScan.variables ?? null) : null}
                />
              </>
            )}
          </div>

          {/* Nebenangebot (gratis): Steckbrief-Plakat — dezent unten */}
          {q.data && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <span className="text-slate-600">
                Tier vermisst? Gratis-Steckbrief-Plakat (Foto, Steckbrief & QR) zum Aushängen.
              </span>
              <a
                href={`/api/pets/${q.data.id}/poster.pdf`}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-brand-700 underline underline-offset-2"
              >
                📄 {t("cats.posterPdf")}
              </a>
            </div>
          )}

          {q.data && (
            <details
              className="mt-12 max-w-3xl"
              onToggle={(e) => {
                if (!(e.target as HTMLDetailsElement).open) setConfirmDelete("");
              }}
            >
              <summary className="text-xs text-slate-400 cursor-pointer select-none hover:text-slate-600 w-fit">
                Erweiterte Optionen
              </summary>
              <div className="card mt-3 p-5 border border-red-200 bg-red-50/40">
                <div className="font-semibold text-red-700">
                  „{q.data.name}" endgültig löschen
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Entfernt das Profil samt allen Fotos, Fundmeldungen und Scans
                  unwiderruflich. Bereits bestellte Halsbänder verlieren ihre
                  Verknüpfung. Diese Aktion kann <strong>nicht</strong> rückgängig
                  gemacht werden — zum Pausieren lieber „{t("cats.deactivate")}" nutzen.
                </p>
                <label className="label mt-4">
                  Zum Bestätigen den Namen „{q.data.name}" eintippen
                </label>
                <input
                  className="input max-w-xs"
                  value={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.value)}
                  placeholder={q.data.name}
                  autoComplete="off"
                />
                {permanentDelete.isError && (
                  <p className="text-sm text-red-600 mt-2">
                    Löschen fehlgeschlagen. Bitte erneut versuchen.
                  </p>
                )}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => permanentDelete.mutate()}
                    disabled={confirmDelete.trim() !== q.data.name || permanentDelete.isPending}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {permanentDelete.isPending ? "Löschen…" : "Endgültig löschen"}
                  </button>
                </div>
              </div>
            </details>
          )}
        </>
      )}


      {lightboxUrl && (
        <div
          role="dialog"
          onClick={() => setLightboxUrl(null)}
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img
            src={lightboxUrl}
            alt=""
            className="max-h-full max-w-full object-contain rounded-lg"
          />
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 bg-white/90 text-slate-900 rounded-full w-10 h-10 text-2xl leading-none"
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function ActiveOrdersInline({ catId, orders }: { catId: string; orders: Order[] }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  if (orders.length === 0) return null;
  return (
    <div className="card p-4 mb-6 bg-amber-50 border-amber-200">
      <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2">
        {t("nav.activeOrders")}
      </div>
      <div className="space-y-2">
        {orders.map((o) => {
          const collarsForCat = o.collars.filter((c) => c.cat.id === catId);
          return (
            <div key={o.id} className="flex items-center justify-between text-sm">
              <div>
                <span className="font-medium">
                  {collarsForCat.length} Halsband{collarsForCat.length !== 1 && "bänder"}
                </span>
                <span className="text-slate-500 ml-2">
                  · {humanDate(o.createdAt, t, locale)} · {(o.priceCents / 100).toFixed(2)} €
                </span>
              </div>
              <span className="text-xs bg-white text-amber-800 px-2 py-0.5 rounded border border-amber-200">
                {t(`orders.status.${o.status}`)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderPromoCard({ catName, catId }: { catName: string; catId: string }) {
  const { t } = useTranslation();
  return (
    <div className="card mb-6 flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg font-medium text-[#1B0E16]">
          {t("cats.orderCollarsFor", { name: catName })}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#6E5E66]">
          <span>{t("cats.promoFeature1")}</span>
          <span>{t("cats.promoFeature2")}</span>
          <span>{t("cats.promoFeature3")}</span>
        </div>
      </div>
      <Link to={`/app/pets/${catId}/order`} className="btn-primary shrink-0">
        {t("cats.orderCollars")}
      </Link>
    </div>
  );
}

function FindingsSection({
  findings,
  scans,
  onOpenPhoto,
  onDelete,
  deletingId,
  onDeleteScan,
  deletingScanId,
}: {
  findings: Finding[];
  scans: ScanEvent[];
  onOpenPhoto: (url: string) => void;
  onDelete: (findingId: string) => void;
  deletingId: string | null;
  onDeleteScan: (scanId: string) => void;
  deletingScanId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [overviewOpen, setOverviewOpen] = useState(true);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});

  const coordFindings = findings.filter((f) => f.lat != null && f.lng != null);
  const points = coordFindings.map((f) => [f.lat!, f.lng!] as [number, number]);

  // Marker angeklickt -> zugehörige Fundmeldung hervorheben + hinscrollen.
  const selectFromMap = (id: string) => {
    setSelectedId(id);
    requestAnimationFrame(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  if (findings.length === 0 && scans.length === 0) {
    return (
      <section id="findings" className="card p-6 text-center text-slate-500">
        <div className="text-3xl mb-2">🐾</div>
        {t("finder.emptySightings")}
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {/* Übersichtskarte als EIGENE Karte (separiert von der Liste) */}
      {coordFindings.length > 0 && (
        <section className="card overflow-hidden">
          <button
            type="button"
            onClick={() => setOverviewOpen((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <span>🗺️ Alle Fundorte ({coordFindings.length})</span>
            <span className="text-xs text-slate-400">{overviewOpen ? "ausblenden ▲" : "anzeigen ▼"}</span>
          </button>
          {overviewOpen && (
            <div className="h-72 border-t border-slate-100">
              <MapContainer center={points[0]!} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={points} />
                {coordFindings.map((f) => {
                  const label = f.message?.trim()
                    ? f.message.trim().slice(0, 28) + (f.message.trim().length > 28 ? "…" : "")
                    : humanDate(f.createdAt, t, locale);
                  return (
                    <Marker
                      key={f.id}
                      position={[f.lat!, f.lng!]}
                      eventHandlers={{ click: () => selectFromMap(f.id) }}
                    >
                      <Tooltip permanent direction="top" offset={[0, -8]} opacity={0.95}>
                        {label}
                      </Tooltip>
                      <Popup>
                        <div className="text-xs">
                          <div className="text-slate-500">{humanDate(f.createdAt, t, locale)}</div>
                          {f.message && <div className="mt-1">{f.message}</div>}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          )}
        </section>
      )}

      <section id="findings" className="card overflow-hidden">
        <header className="px-6 py-4 bg-slate-50 text-slate-700 flex items-center gap-2">
          <div className="text-xl">🐾</div>
          <h2 className="font-semibold">
            {t("finder.sightingsCount", { count: findings.length })}
            {scans.length > 0 && (
              <span className="font-normal text-slate-400"> · {t("finder.scansCount", { count: scans.length })}</span>
            )}
          </h2>
        </header>

        <div className="divide-y divide-slate-100">
          {[
            ...findings.map((f) => ({ kind: "finding" as const, at: f.createdAt, f })),
            ...scans.map((s) => ({ kind: "scan" as const, at: s.createdAt, s })),
          ]
            .sort((a, b) => (a.at < b.at ? 1 : -1))
            .map((item) =>
              item.kind === "finding" ? (
                <FindingCard
                  key={`f-${item.f.id}`}
                  finding={item.f}
                  onOpenPhoto={onOpenPhoto}
                  onDelete={onDelete}
                  deleting={deletingId === item.f.id}
                  selected={item.f.id === selectedId}
                  innerRef={(el) => {
                    cardRefs.current[item.f.id] = el;
                  }}
                />
              ) : (
                <ScanRow
                  key={`s-${item.s.id}`}
                  createdAt={item.s.createdAt}
                  device={item.s.finderDevice}
                  onDelete={() => onDeleteScan(item.s.id)}
                  deleting={deletingScanId === item.s.id}
                />
              ),
            )}
        </div>
      </section>
    </div>
  );
}

// Passt den Kartenausschnitt einmalig so an, dass alle Marker sichtbar sind.
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    const first = points[0];
    if (!first) return;
    if (points.length === 1) {
      map.setView(first, 15);
    } else {
      map.fitBounds(points, { padding: [30, 30], maxZoom: 16 });
    }
    // einmalig beim Mount; Fundliste ändert sich während der Ansicht nicht
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}

interface OwnerCollar {
  id: string;
  publicId: string | null;
  type: "collar" | "tag";
  color: string | null;
  active: boolean;
}

// Owner-Sektion: einzelne Halsbänder/Anhänger sperren (z.B. verlorenes Stück).
function CollarsSection({ catId }: { catId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["collars", catId],
    queryFn: () => api<OwnerCollar[]>(`/api/pets/${catId}/collars`),
  });
  const toggle = useMutation({
    mutationFn: ({ collarId, active }: { collarId: string; active: boolean }) =>
      api(`/api/pets/${catId}/collars/${collarId}`, {
        method: "PATCH",
        body: JSON.stringify({ active }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collars", catId] }),
  });

  if (!q.data || q.data.length === 0) return null;

  const labelFor = (c: OwnerCollar) =>
    c.type === "tag"
      ? "Mini-Anhänger"
      : `Halsband${c.color ? ` · ${t(`collarColors.${c.color}`)}` : ""}`;

  return (
    <div className="card p-5 mb-6">
      <div className="label mb-3">Meine Halsbänder & Anhänger</div>
      <div className="space-y-2">
        {q.data.map((c) => (
          <div
            key={c.id}
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm ${
              c.active ? "border-slate-200" : "border-amber-200 bg-amber-50/50"
            }`}
          >
            <div className="min-w-0">
              <span className="font-medium text-slate-800">{labelFor(c)}</span>
              {c.publicId && <code className="ml-2 text-xs text-slate-500">{c.publicId}</code>}
              {!c.active && <span className="ml-2 text-xs font-medium text-amber-700">gesperrt</span>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {c.active ? (
                <button
                  type="button"
                  onClick={() => toggle.mutate({ collarId: c.id, active: false })}
                  disabled={toggle.isPending}
                  className="text-xs font-medium text-red-600 underline underline-offset-2"
                >
                  sperren
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => toggle.mutate({ collarId: c.id, active: true })}
                    disabled={toggle.isPending}
                    className="text-xs font-medium text-slate-600 underline underline-offset-2"
                  >
                    entsperren
                  </button>
                  <Link
                    to={`/app/pets/${catId}/order`}
                    className="text-xs font-medium text-brand-700 underline underline-offset-2"
                  >
                    Ersatz nachbestellen
                  </Link>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Stück verloren? Sperre genau dieses — die anderen bleiben aktiv.
      </p>
    </div>
  );
}

function ScanRow({
  createdAt,
  device,
  onDelete,
  deleting = false,
}: {
  createdAt: string;
  device?: string | null;
  onDelete: () => void;
  deleting?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [confirm, setConfirm] = useState<0 | 1 | 2>(0);
  // Desktop-Scan = wahrscheinlich Fehlalarm (kein Finder unterwegs). Dezent
  // abgeschwächt darstellen + markieren; Benachrichtigung entfällt serverseitig.
  const falsePositive = isDesktopScan(device);
  return (
    <div className={`flex items-start gap-4 p-4 sm:p-5 ${falsePositive ? "opacity-60" : ""}`}>
      <div className="shrink-0 w-20 h-20 rounded-xl bg-slate-50 flex items-center justify-center text-2xl text-slate-300">
        🔍
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold text-slate-500" title={fullTimestamp(createdAt, locale)}>
              {humanDate(createdAt, t, locale)}
            </div>
            {device && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                📱 {device}
              </span>
            )}
            {falsePositive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                {t("finder.likelyFalsePositive")}
              </span>
            )}
          </div>
          {confirm === 0 && (
            <button
              type="button"
              onClick={() => setConfirm(1)}
              aria-label={t("finder.deleteScan")}
              title={t("finder.deleteScan") ?? "Löschen"}
              className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <DataFlags className="mt-2" />
        <div className="mt-2 text-xs text-slate-400">{t("finder.scanned")}</div>

        {/* Doppelte Löschbestätigung wie bei Fundmeldungen. */}
        {confirm === 1 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
            <span className="text-amber-900">{t("finder.deleteScanConfirm1")}</span>
            <button
              type="button"
              onClick={() => setConfirm(2)}
              className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
            >
              {t("common.continue")}
            </button>
            <button
              type="button"
              onClick={() => setConfirm(0)}
              className="text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
        {confirm === 2 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm">
            <span className="font-medium text-red-800">{t("finder.deleteConfirm2")}</span>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? t("finder.deleting") : t("finder.deleteFinal")}
            </button>
            <button
              type="button"
              onClick={() => setConfirm(0)}
              disabled={deleting}
              className="text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Klassisches Papierkorb-Icon (Feather/Heroicons-Stil), kein Emoji.
function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function FindingCard({
  finding: f,
  onOpenPhoto,
  onDelete,
  deleting = false,
  selected = false,
  innerRef,
}: {
  finding: Finding;
  onOpenPhoto: (url: string) => void;
  onDelete: (findingId: string) => void;
  deleting?: boolean;
  selected?: boolean;
  innerRef?: (el: HTMLElement | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [mapOpen, setMapOpen] = useState(false);
  // Zwei-Stufen-Bestätigung: 0 = aus, 1 = erste Rückfrage, 2 = endgültig.
  const [confirm, setConfirm] = useState<0 | 1 | 2>(0);
  const hasCoords = f.lat != null && f.lng != null;
  const geoLink = hasCoords ? `geo:${f.lat},${f.lng}?q=${f.lat},${f.lng}` : null;
  // Eindeutiges Signal: locationAddress gesetzt => manuelle Adresse, sonst GPS.
  const isManualAddress = !!f.locationAddress;
  const isGps = hasCoords && !isManualAddress;

  return (
    <article
      ref={innerRef}
      className={`p-4 sm:p-5 scroll-mt-4 transition-colors ${
        selected ? "bg-brand-50 ring-1 ring-inset ring-brand-300" : "hover:bg-slate-50/70"
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Foto oder Platzhalter-Kachel (gleiche Größe -> ausgerichtete Liste) */}
        {f.photoUrl ? (
          <button
            type="button"
            onClick={() => onOpenPhoto(f.photoUrl!)}
            className="shrink-0 rounded-xl overflow-hidden ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            aria-label="Foto vergrößern"
          >
            <img
              src={f.photoUrl}
              alt=""
              className="w-20 h-20 object-cover hover:opacity-90 transition-opacity"
            />
          </button>
        ) : (
          <div className="shrink-0 w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center text-3xl text-slate-300">
            🐾
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Kopf: Zeitstempel prominent + Badges rechts */}
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-slate-900" title={fullTimestamp(f.createdAt, locale)}>
              {humanDate(f.createdAt, t, locale)}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {f.distanceKm != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-800 ring-1 ring-brand-100">
                  📏 {t("finder.distanceBadge", { km: f.distanceKm })}
                </span>
              )}
              {f.notifiedSms && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100">
                  SMS
                </span>
              )}
              {confirm === 0 && (
                <button
                  type="button"
                  onClick={() => setConfirm(1)}
                  aria-label={t("finder.deleteFinding")}
                  title={t("finder.deleteFinding") ?? "Löschen"}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Daten-Status-Chips */}
          <DataFlags
            location={isGps ? "gps" : isManualAddress ? "address" : "none"}
            comment={!!f.message}
            photo={!!f.photoUrl}
            className="mt-2"
          />

          {/* Nachricht des Finders als Message-Box */}
          {f.message && (
            <div className="mt-2.5 max-w-[92%] rounded-2xl rounded-tl-sm border border-slate-200 bg-slate-50 px-3.5 py-2.5">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <span aria-hidden>💬</span> Nachricht vom Finder
              </div>
              <p className="text-[15px] leading-snug text-slate-800 whitespace-pre-wrap">{f.message}</p>
            </div>
          )}

          {/* Rückrufnummer, falls der Finder freiwillig eine hinterlassen hat */}
          {f.finderPhone && (
            <div className="mt-2.5 rounded-2xl border border-brand-100 bg-brand-50 px-3.5 py-2.5">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
                📞 Finder ist erreichbar
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[15px] font-semibold text-slate-800">{f.finderPhone}</span>
                <a
                  href={`tel:${f.finderPhone.replace(/[^\d+]/g, "")}`}
                  className="rounded-lg border border-brand-200 bg-white px-3 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-50"
                >
                  Anrufen
                </a>
                {f.finderWhatsapp && whatsappLink(f.finderPhone) && (
                  <a
                    href={whatsappLink(f.finderPhone)!}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-[#25D366] px-3 py-1 text-xs font-semibold text-white hover:brightness-95"
                  >
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Standort */}
          {isManualAddress ? (
            <p className="mt-2.5 text-sm text-slate-600">
              📍 {f.locationAddress}{" "}
              <span className="text-slate-400">
                {hasCoords ? "(ungefähre Adresse)" : "(nicht auf Karte gefunden)"}
              </span>
            </p>
          ) : hasCoords ? (
            <p className="mt-2.5 text-sm text-slate-600">
              📍 {f.lat!.toFixed(5)}, {f.lng!.toFixed(5)}{" "}
              <span className="text-slate-400">(±{Math.round(f.accuracyM ?? 0)} m)</span>
            </p>
          ) : null}

          {/* Karte je Fund: aufklappbar; Maps-Link erst bei offener Karte */}
          {hasCoords && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setMapOpen((v) => !v)}
                className="text-xs font-medium text-brand-700 hover:text-brand-800"
              >
                {mapOpen ? "🗺️ Karte ausblenden" : "🗺️ Auf Karte anzeigen"}
              </button>
              {mapOpen && (
                <div className="mt-2">
                  <div className="h-48 overflow-hidden rounded-xl border border-slate-200">
                    <MapContainer
                      center={[f.lat!, f.lng!]}
                      zoom={15}
                      scrollWheelZoom={false}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[f.lat!, f.lng!]} />
                    </MapContainer>
                  </div>
                  {geoLink && (
                    <a href={geoLink} className="mt-1.5 inline-block text-xs underline text-brand-700">
                      {t("finder.openInMaps")}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Doppelte Löschbestätigung: erst Rückfrage, dann endgültig. */}
          {confirm === 1 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
              <span className="text-amber-900">{t("finder.deleteConfirm1")}</span>
              <button
                type="button"
                onClick={() => setConfirm(2)}
                className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
              >
                {t("common.continue")}
              </button>
              <button
                type="button"
                onClick={() => setConfirm(0)}
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                {t("common.cancel")}
              </button>
            </div>
          )}
          {confirm === 2 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm">
              <span className="font-medium text-red-800">{t("finder.deleteConfirm2")}</span>
              <button
                type="button"
                onClick={() => onDelete(f.id)}
                disabled={deleting}
                className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? t("finder.deleting") : t("finder.deleteFinal")}
              </button>
              <button
                type="button"
                onClick={() => setConfirm(0)}
                disabled={deleting}
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                {t("common.cancel")}
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

// Daten-Status-Chip als Pille: aktiv (vorhanden) = grün mit ✓, sonst dezent grau.
function DataFlag({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
        on
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-slate-100 text-slate-400"
      }`}
    >
      <span aria-hidden>{on ? "✓" : "○"}</span>
      {label}
    </span>
  );
}

// Einheitliche Status-Zeile für jeden Timeline-Eintrag (Fund oder reiner Scan).
// Standort wird als EINE Pille gezeigt (GPS / Adresse / Kein Standort) — kein
// zusätzliches graues Adresse-Icon, wenn GPS aktiv ist.
function DataFlags({
  location = "none",
  comment = false,
  photo = false,
  className = "",
}: {
  location?: "gps" | "address" | "none";
  comment?: boolean;
  photo?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <DataFlag label="QR-Scan" on />
      {location === "gps" ? (
        <DataFlag label="GPS" on />
      ) : location === "address" ? (
        <DataFlag label="Adresse" on />
      ) : (
        <DataFlag label="Standort" on={false} />
      )}
      <DataFlag label="Kommentar" on={comment} />
      <DataFlag label="Foto" on={photo} />
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
